import { showApiError, showSuccess } from "@/lib/toast";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { addCampaignCondition, deleteCampaignCondition, getCampaignCondition } from "@/sideEffects/api";
import { CampaignDetails } from "@/lib/types";
import { useSelector } from "react-redux";
import { selectActiveFirm } from "@/store/appSelectors";
import { LoadingSmall } from "@/components/LoadingSmall";
import { CampaignConditionsMetaForm, FormSchemaType } from "./CampaignConditionsMetaForm";

interface Props {
  campaignDetails: CampaignDetails;
}

export const CampaignUpdateConditionalConditions: React.FC<Props> = ({ campaignDetails }) => {
  const { t } = useTranslation();
  const activeFirm = useSelector(selectActiveFirm);

  const { data: campaignConditions, isLoading: isCampaignConditionsLoading } = useQuery({
    queryKey: ["getCampaignConditions", campaignDetails.id],
    queryFn: () =>
      getCampaignCondition(
        {
          numberOfObjectsPerPage: 999999,
          pageNumber: 1,
        },
        {
          Firm_Id: String(activeFirm?.firmGuid),
          Campaign_Id: campaignDetails.id,
        }
      ),
  });

  const queryClient = useQueryClient();
  const updateCampaignMutation = useMutation({
    mutationFn: async (values: FormSchemaType) => {
      const campaignIntId = campaignDetails.intId;

      const existingCampaignConditions = await getCampaignCondition(
        {
          numberOfObjectsPerPage: 999999,
          pageNumber: 1,
        },
        {
          Firm_Id: String(activeFirm?.firmGuid),
          Campaign_Id: campaignDetails.id,
        }
      );

      for (const condition of existingCampaignConditions.data.result) {
        await deleteCampaignCondition(condition.id, String(activeFirm?.firmGuid));
      }

      for (const condition of values.conditions || []) {
        await addCampaignCondition({
          Firm_Id: String(activeFirm?.firmGuid),
          campaign_Id: campaignIntId,
          cameraAges: JSON.stringify(condition.ageRanges),
          genders: JSON.stringify(condition.genders),
          cameraEmotions: JSON.stringify(condition.emotions),
          areas: JSON.stringify(condition.areas),
        });
      }

      return Promise.resolve();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["getCampaign"],
      });
      queryClient.invalidateQueries({
        queryKey: ["getCampaignDetails"],
      });
      showSuccess(t("common.updatedSuccess"));
    },
    onError: showApiError,
  });

  if (isCampaignConditionsLoading) {
    return <LoadingSmall />;
  }

  return (
    <CampaignConditionsMetaForm
      buttonText={t("common.update")}
      onSubmit={(values) => updateCampaignMutation.mutate(values)}
      isLoading={updateCampaignMutation.isPending}
      defaultValues={{
        conditions: campaignConditions?.data.result.map((condition) => ({
          emotions: condition.cameraEmotions.map((emotion) => emotion.value),
          ageRanges: condition.cameraAges.map((age) => age.value),
          genders: condition.genders.map((gender) => gender.value),
          areas: condition.areas.map((area) => area.intId),
        })),
      }}
      showTypeSelect={false}
    />
  );
};
